"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { LuCoins, LuSend } from "react-icons/lu";

import type { TopBountyRecord } from "@/backend/focus";
import { getTopBounty } from "@/backend/focus";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { Button } from "@/components/ui/button";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import {
  APP_NAME_GENERIC,
  SocialNetworkMap,
  SocialNetworkType,
  SOCIALS_LIST,
} from "@/utils/constants";
import { formatUSDCents } from "@/utils/currency";
import { DIALOG } from "@/utils/dialog-routes";
import { getUserName } from "@/utils/usernames";

import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "../ui/use-toast";

const TABLE_HEADERS = ["Username", "Wallet Balance", "Bounty Request"];

export const TopBountyRequests = ({
  username,
  baseAmount,
  userId,
}: {
  username: string;
  baseAmount: number;
  userId: string;
}) => {
  const [topBountiesRequestList, setTopBountiesRequestList] = useState<
    TopBountyRecord[]
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchTopBounties = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const topBountiesResponse = await getTopBounty({
        userId,
        params: {
          platform: SOCIALS_LIST.TWITTER,
          limit: 10,
        },
      });
      setTopBountiesRequestList(topBountiesResponse);
      setLoading(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch top bounties request`,
        details: createToastErrorDetails(error),
      });
    }
  }, [userId]);

  useEffect(() => {
    fetchTopBounties();
  }, [fetchTopBounties]);

  // Filter duplicated records with the same requester username and keep the one with the biggest SuggestedBountyUsdCents
  const uniqueTopBountiesRequestList = topBountiesRequestList?.reduce(
    (acc, current) => {
      const requesterUsername = current.RequesterUsername;
      if (
        !acc[requesterUsername] ||
        acc[requesterUsername].SuggestedBountyUsdCents <
          current.SuggestedBountyUsdCents
      ) {
        acc[requesterUsername] = current;
      }
      return acc;
    },
    {} as Record<string, TopBountyRecord>,
  );

  const filteredTopBountiesRequestList = uniqueTopBountiesRequestList
    ? Object.values(uniqueTopBountiesRequestList)
    : [];

  return (
    <>
      <div
        className="
          mb-2 flex items-center gap-2 text-lg font-semibold
          text-muted-foreground
        "
      >
        <LuCoins className="text-muted" />
        <h2 className="font-sans">Top Bounty Requests</h2>
      </div>
      <p className="mb-4 text-left text-xs/5 text-muted">
        All {APP_NAME_GENERIC} users who have requested a new bounty offer for @
        {username}
      </p>
      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {TABLE_HEADERS.map((e) => (
                <TableHead
                  key={e}
                  className="
                    px-2 text-left text-xs text-muted
                    last:text-right
                  "
                >
                  {e}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ) : filteredTopBountiesRequestList &&
              filteredTopBountiesRequestList?.length > 0 ? (
              filteredTopBountiesRequestList?.map((topRequest) => {
                const socialNetworkKey =
                  topRequest.Platform.toLowerCase() as keyof typeof SocialNetworkType;

                const socialNetworkIconKey =
                  SocialNetworkType[socialNetworkKey];

                const Icon = SocialNetworkMap[socialNetworkIconKey].icon;

                return (
                  <TableRow key={topRequest.RequesterUsername}>
                    <TableCell
                      className="
                      flex items-center gap-2 text-left text-xs
                    "
                    >
                      <Image
                        src="/assets/logo-deso-mark.svg"
                        width={15}
                        height={15}
                        alt="Deso"
                      />
                      <span
                        className="
                          inline-block max-w-[130px] truncate overflow-x-hidden
                        "
                      >
                        {getUserName({
                          account: {
                            username: topRequest.RequesterUsername,
                          },
                        })}
                      </span>
                    </TableCell>
                    <TableCell
                      className="
                        font-shadow-green text-left text-xs text-green-500
                      "
                    >
                      {formatUSDCents(topRequest.RequesterWealthUsdCents)}
                    </TableCell>
                    <TableCell
                      className="
                      font-shadow-green text-left text-green-500
                    "
                    >
                      <div
                        className="
                        flex items-center justify-end gap-2 text-xs
                      "
                      >
                        {formatUSDCents(topRequest.SuggestedBountyUsdCents)}
                        <Icon className="text-muted" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <p className="text-center">No top bounties found.</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        size="sm"
        className="my-4 flex w-full items-center gap-2"
        variant="outline"
        onClick={() => {
          setDialog(DIALOG.BountyRequest, {
            open: true,
            params: {
              baseAmount: String(baseAmount),
              userId,
              username,
            },
            props: {
              onSubmitted: fetchTopBounties,
            },
          });
        }}
      >
        <LuSend />
        Submit Bounty Request
      </Button>
    </>
  );
};
